import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { TranslationService } from '../../services/translation.service';
import DateTimePicker, { type IDateTimePickerState } from '../Shared/DateTimePicker';
import { ConfigurationService } from '../../services/configuration.service';
import { DateHelper } from '../../helpers/date.helper';
import ValidationInfo from '../../validation/validation-info';
import GeneralError from '../Shared/GeneralError';
import GeneralInformation from '../Shared/GeneralInformation';
import FromToSelector, { type IFromToSelector, type IFromToSelectorState } from '../Shared/FromToSelector';
import { LocationType } from '../../models/location-type.enum';
import type IValidatable from '../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import { AnalyticsService } from '../../services/analytics-service';
import type { IJourney } from '../../models/journey.model';
import { ApiPlaneraResaService } from '../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../helpers/error-response.helper';
import { DateTimeRelatesTo } from '../../models/date-time-relates-to.enum';
import { JourneyHelper } from '../../helpers/journey.helper';
import Spinner from '../Shared/Spinner';
import PaginatedList from '../Shared/JourneySearchResults/PaginatedList';
import SingleTicketsResultItem from './components/SingleTicketsResultItem';
import DateHeader from '../Shared/JourneySearchResults/DateHeader';
import { type IErrorResponse, ErrorLevel } from '../../models/error-response.model';
import { ErrorHandlerHelper } from '../../helpers/error-handler.helper';
import { Status } from '../../hooks/fetch.hook';
import { JourneySearchHelper } from '../../helpers/journey-search.helper';
import { IPaginationLinks } from '../../models/pagination-links.model';

function SingleTickets() {
  // === STATE ===
  const [fromToSelectorState, setFromToSelectorState] = useState<IFromToSelectorState>({
    value: {
      fromLocation: undefined,
      toLocation: undefined,
    },
    validationInfo: new ValidationInfo(),
  });
  const [dateTimePickerState, setDateTimePickerState] = useState<IDateTimePickerState>({
    value: DateHelper.roundToNearestFiveMinutes(DateTime.now()),
    validationInfo: new ValidationInfo(),
  });
  const [searchJourneysStatus, setSearchJourneysStatus] = useState<Status>(Status.notperformed);
  const [receivedJourneys, setReceivedJourneys] = useState<IJourney[]>([]);
  const [receivedPaginationLinks, setReceivedPaginationLinks] = useState<IPaginationLinks | undefined>(undefined);
  const [isSearchingPrevious, setIsSearchingPrevious] = useState<boolean>(false);
  const [isSearchingNext, setIsSearchingNext] = useState<boolean>(false);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);

  // === REFS ===
  const fromToSelectorRef = useRef<IFromToSelector>(null);
  const dateTimePickerRef = useRef<IValidatable>(null);

  // === VARIABLES ===
  const minDateTime = DateHelper.parseWithFallback(ConfigurationService.get('travelplanner.validitytimefrom'), DateTime.now().startOf('day').minus({ days: 1 }));
  const maxDateTime = DateHelper.parseWithFallback(ConfigurationService.get('travelplanner.validitytimeuntil'), DateTime.now().startOf('day').plus({ days: 90 }));
  const uniqueJourneyDates = JourneyHelper.getAllUniqueDates(receivedJourneys);

  // === HELPER FUNCTIONS ===
  function getScreenReaderSearchStatus() {
    if (searchJourneysStatus === Status.notperformed) {
      return '';
    }
    if (searchJourneysStatus === Status.loading) {
      return TranslationService.get('common.searching');
    }
    return TranslationService.get('common.showingxresults').replace('{0}', (receivedJourneys.length ? receivedJourneys.length : 0).toString());
  }

  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: fromToSelectorRef.current!, validationInfo: fromToSelectorState.validationInfo, setState: setFromToSelectorState },
      { component: dateTimePickerRef.current!, validationInfo: dateTimePickerState.validationInfo, setState: setDateTimePickerState },
    ];
  }

  // === EVENT HANDLERS ===
  async function handleSearchClick(): Promise<void> {
    if (searchJourneysStatus === Status.loading) return;

    if (!fromToSelectorState.value.fromLocation || !fromToSelectorState.value.toLocation) {
      setSearchJourneysStatus(Status.notperformed);
      return;
    }

    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      setSearchJourneysStatus(Status.notperformed);
      return;
    }

    setSearchJourneysStatus(Status.loading);
    fromToSelectorRef.current?.addSelectedItemsToAutocompleteHistory();
    setReceivedJourneys([]);
    setReceivedPaginationLinks(undefined);
    AnalyticsService.addEvent(AnalyticsService.FindTicket, 'Sök enkelbiljett');

    const fromLocationDetails = JourneySearchHelper.getLocationParameters(fromToSelectorState.value.fromLocation);
    const toLocationDetails = JourneySearchHelper.getLocationParameters(fromToSelectorState.value.toLocation);

    try {
      const result = await ApiPlaneraResaService.getJourneys(
        fromLocationDetails.gid,
        fromLocationDetails.name,
        fromLocationDetails.latitude,
        fromLocationDetails.longitude,
        toLocationDetails.gid,
        toLocationDetails.name,
        toLocationDetails.latitude,
        toLocationDetails.longitude,
        dateTimePickerState.value,
        DateTimeRelatesTo.departure,
        undefined,
        3,
        undefined,
        undefined,
        false,
        undefined,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        false
      );
      const journeysWithTripLegs = result.results.filter((x) => x.tripLegs);
      setReceivedJourneys(JourneyHelper.setJourneyIds(journeysWithTripLegs, 0, false));
      setReceivedPaginationLinks(result.links);
      setSearchJourneysStatus(Status.success);
      setErrorResponse(undefined);
      if (journeysWithTripLegs.length < 1) {
        AnalyticsService.addEvent(AnalyticsService.FindTicket, AnalyticsService.ZeroResultsAction);
      }
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      AnalyticsService.addEvent(AnalyticsService.FindTicket, AnalyticsService.ErrorAction, `Sök biljettpris - ${AnalyticsService.InternalErrorLabel}`);
    }
  }

  async function handlePreviousClick(): Promise<void> {
    if (receivedPaginationLinks?.previous === undefined) return;
    try {
      setIsSearchingPrevious(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.previous);
      setReceivedJourneys((ps) => JourneyHelper.setJourneyIds(result.results, ps[0].id - 1, true).concat(ps));
      setReceivedPaginationLinks((ps) => (ps ? { ...ps, previous: result.links?.previous } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
    } finally {
      setIsSearchingPrevious(false);
    }
  }

  async function handleNextClick(): Promise<void> {
    if (receivedPaginationLinks?.next === undefined) return;
    try {
      setIsSearchingNext(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.next);
      setReceivedJourneys((ps) => ps.concat(JourneyHelper.setJourneyIds(result.results, ps[ps.length - 1].id + 1, false)));
      setReceivedPaginationLinks((ps) => (ps ? { ...ps, next: result.links?.next } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
    } finally {
      setIsSearchingNext(false);
    }
  }

  // === CONSTANTS ===
  const showSearchDetails = false; // TEMP
  const priceDisclaimerId = 'price-disclaimer';

  return (
    <div className={`single-tickets-v2 ${!showSearchDetails ? 'travel-planner-v3__search-details--invisible' : ''}`} role="search" aria-label={TranslationService.get('singleticket.searchlabel')}>
      {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
      <span className="visually-hidden" aria-live="polite">
        {getScreenReaderSearchStatus()}
      </span>
      <div className="mb-3">
        <FromToSelector
          id={'from-to-selector'}
          ref={fromToSelectorRef}
          locationTypes={[LocationType.stopArea, LocationType.metaStation]}
          placeholderText={TranslationService.get('common.stop')}
          enableCurrentPosition={true}
          state={fromToSelectorState}
          onChange={(value) => setFromToSelectorState({ value, validationInfo: new ValidationInfo() })}
        />
      </div>
      <div className={`bg-white ${!showSearchDetails ? 'travel-planner-v3__search-details--invisible' : ''}`}>
        <div className="mb-3 mb-md-4">
          <DateTimePicker
            ref={dateTimePickerRef}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            showAsteriskOnRequiredFields={true}
            state={dateTimePickerState}
            layoutLgColumn={true}
            onChange={(value) => {
              setDateTimePickerState({ ...dateTimePickerState, value });
            }}
          />
        </div>
        <div className="d-grid">
          <button id="searchbutton" className="btn btn-primary" aria-disabled={searchJourneysStatus === Status.loading} type="button" onClick={handleSearchClick}>
            {TranslationService.get('common.searchfare')}
          </button>
        </div>
        {searchJourneysStatus === Status.loading && <Spinner className="mt-4" />}
        <GeneralInformation
          showInformation={
            (receivedJourneys.length === 0 && searchJourneysStatus === Status.success) || (searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.information)
          }
          informationText={errorResponse?.errorMessage}
          className="mt-4 text-center"
        />
        {searchJourneysStatus === Status.success && (
          <div className="mt-4">
            {receivedJourneys.length > 0 && (
              <>
                <PaginatedList
                  className="fullwidth-xs"
                  previousButtonDisabled={receivedPaginationLinks?.previous === undefined}
                  nextButtonDisabled={receivedPaginationLinks?.next === undefined}
                  showSpinnerTop={isSearchingPrevious}
                  showSpinnerBottom={isSearchingNext}
                  onPreviousClick={handlePreviousClick}
                  onNextClick={handleNextClick}
                >
                  {uniqueJourneyDates.map((date) => (
                    <li key={date.toISODate()}>
                      <DateHeader date={date} uniqueJourneyDates={uniqueJourneyDates} />
                      <ul className="list-unstyled">
                        {JourneyHelper.getJourneysForDate(receivedJourneys, date).map((journey, index) => (
                          <SingleTicketsResultItem key={journey.id} journey={journey} index={index} priceDisclaimerId={priceDisclaimerId}></SingleTicketsResultItem>
                        ))}
                      </ul>
                    </li>
                  ))}
                </PaginatedList>
                <div id={priceDisclaimerId} className="">
                  {TranslationService.get('singleticket.pricedisclaimer')}
                </div>
              </>
            )}
          </div>
        )}
        <GeneralError showError={searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.error} errorText={errorResponse?.errorMessage} className="mt-4 text-center" />
      </div>
    </div>
  );
}

export default SingleTickets;
